/**
 * Signed-in user's data: profile, active course, lessons, progress, XP and cards.
 * Writes update local state immediately and go to the offline queue (data/sync.ts).
 * The last snapshot is kept in localStorage so the app opens offline.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  countNewSeen as countNewSeenByLang,
  fetchBooks,
  fetchCards,
  fetchCourses,
  fetchLessonMetas,
  fetchNewSeenTodayIds,
  fetchProfile,
  fetchProgress,
  fetchXpEvents,
  updateProfile as saveProfile,
  type Book,
  type Card,
  type CardBack,
  type CardFront,
  type CardKind,
  type Course,
  type LessonMeta,
  type Profile,
  type ProfilePatch,
  type ProgressRecord,
  type XpEventRow,
} from '../data/api.ts';
import { flush, pendingOps, queueWrite } from '../data/sync.ts';
import { readJson, readString, writeJson, writeString } from '../data/storage.ts';
import { prefetchLeague } from '../data/league.ts';
import { runAchievementCheck } from '../data/achievements.ts';
import { chooseCourse, courseSlice } from '../core/courses.ts';
import { appDay, dayStartIso } from '../core/date.ts';
import { CardState, newCardFields, review, type Rating } from '../core/fsrs.ts';
import { xpForReview } from '../core/progress.ts';
import { parseStepsState } from '../core/lesson-plan.ts';
import type { CardRow, Op } from '../core/outbox.ts';
import { cardId } from '../core/uuid.ts';
import { reviewAward, type XpAward } from '../core/xp-award.ts';

interface Snapshot {
  profile: Profile;
  courses: Course[];
  activeCourseId: string | null;
  /** Every course's books and lesson metadata; `books`/`lessons` are the active course's. */
  allBooks: Book[];
  allLessons: LessonMeta[];
  books: Book[];
  lessons: LessonMeta[];
  progress: Record<string, ProgressRecord>;
  xpEvents: XpEventRow[];
  cards: Card[];
  newSeenToday: Record<string, number>;
  /** App day `newSeenToday` counts for; stale counts from a cached snapshot are ignored. */
  newSeenDay: string;
}

export interface NewCard {
  kind: CardKind;
  ref_id: string;
  lang: string;
  front: CardFront;
  back: CardBack;
}

interface AppDataValue {
  status: 'loading' | 'ready' | 'error';
  error: string | null;
  /** True while showing the cached snapshot because the server could not be reached. */
  stale: boolean;
  data: Snapshot | null;
  activeCourse: Course | null;
  refresh: () => Promise<void>;
  /** Switches instantly; the choice is saved to the server in the background. */
  setActiveCourse: (courseId: string) => void;
  updateProfile: (patch: ProfilePatch) => Promise<void>;
  /** Optimistically adds `amount` and asks the server to grant `award` (it decides the real amount). */
  addXp: (amount: number, award: XpAward | null) => void;
  saveProgress: (record: ProgressRecord) => void;
  addCards: (cards: readonly NewCard[]) => number;
  reviewCard: (card: Card, rating: Rating) => Card;
  /** Sends queued writes, then lets the server award newly earned badges. */
  checkAchievements: () => void;
}

const AppDataContext = createContext<AppDataValue | null>(null);

const snapshotKey = (userId: string) => `soile.snapshot.v2.${userId}`;
/** Course picked on this device but maybe not yet saved to the profile (offline, error). */
const courseChoiceKey = (userId: string) => `soile.activeCourse.${userId}`;
/** Snapshot writes are coalesced: serialising every card on each XP tick is wasteful. */
const SNAPSHOT_WRITE_DELAY_MS = 600;

type StoredCard = Omit<Card, 'due' | 'last_review' | 'created_at'> & {
  due: string;
  last_review: string | null;
  created_at: string;
};
type StoredSnapshot = Omit<Snapshot, 'cards' | 'allBooks' | 'allLessons'> & {
  cards: StoredCard[];
  allBooks?: Book[];
  allLessons?: LessonMeta[];
};

function reviveSnapshot(stored: StoredSnapshot): Snapshot {
  return {
    ...stored,
    // Snapshots saved before the whole catalogue was loaded only hold the active course.
    allBooks: stored.allBooks ?? stored.books,
    allLessons: stored.allLessons ?? stored.lessons,
    cards: stored.cards.map((c) => ({
      ...c,
      due: new Date(c.due),
      last_review: c.last_review ? new Date(c.last_review) : null,
      created_at: new Date(c.created_at),
    })),
  };
}

async function loadSnapshot(userId: string): Promise<Snapshot> {
  const now = new Date();
  // One parallel wave: every request is independent, so the slowest one sets the wait.
  const [profile, courses, allBooks, allLessons, progressList, xpEvents, cards, newSeenIds] = await Promise.all([
    fetchProfile(userId),
    fetchCourses(),
    fetchBooks(),
    fetchLessonMetas(),
    fetchProgress(),
    fetchXpEvents(now),
    fetchCards(),
    fetchNewSeenTodayIds(now),
  ]);
  const course = chooseCourse(courses, readString(courseChoiceKey(userId)), profile.active_course_id);
  return {
    profile,
    courses,
    activeCourseId: course?.id ?? null,
    allBooks,
    allLessons,
    ...courseSlice(allBooks, allLessons, course?.id ?? null),
    progress: Object.fromEntries(progressList.map((p) => [p.lesson_id, p])),
    xpEvents,
    cards,
    newSeenToday: countNewSeenByLang(newSeenIds, cards),
    newSeenDay: appDay(now),
  };
}

/** Saves a course choice made on this device; forgets it once the profile holds it. */
async function syncCourseChoice(userId: string, serverCourseId: string | null): Promise<string | null> {
  const choice = readString(courseChoiceKey(userId));
  if (!choice) return serverCourseId;
  if (choice !== serverCourseId) await saveProfile(userId, { active_course_id: choice });
  writeString(courseChoiceKey(userId), null);
  return choice;
}

function countNewSeen(d: Snapshot, lang: string, today: string): Pick<Snapshot, 'newSeenToday' | 'newSeenDay'> {
  const counts = d.newSeenDay === today ? d.newSeenToday : {};
  return { newSeenToday: { ...counts, [lang]: (counts[lang] ?? 0) + 1 }, newSeenDay: today };
}

function fromCardRow(row: CardRow, previous: Card | undefined, now: Date): Card {
  return {
    ...row,
    front: row.front as CardFront,
    back: row.back as CardBack,
    state: row.state as Card['state'],
    due: new Date(row.due),
    last_review: row.last_review ? new Date(row.last_review) : null,
    suspended: previous?.suspended ?? false,
    created_at: previous?.created_at ?? now,
  };
}

/**
 * Re-applies writes still waiting in the outbox, so a refresh that lands before they are
 * flushed does not roll local state back (and later re-queue an older progress snapshot).
 */
function withPending(snapshot: Snapshot, ops: readonly Op[], now: Date): Snapshot {
  if (ops.length === 0) return snapshot;
  const progress = { ...snapshot.progress };
  const cards = new Map(snapshot.cards.map((c) => [c.id, c]));
  const xpEvents = [...snapshot.xpEvents];
  const newSeenToday = { ...snapshot.newSeenToday };
  const todayStart = dayStartIso(appDay(now));
  for (const op of ops) {
    switch (op.type) {
      case 'progress':
        progress[op.row.lesson_id] = { ...op.row, steps: parseStepsState(op.row.steps) };
        break;
      case 'card':
        cards.set(op.row.id, fromCardRow(op.row, cards.get(op.row.id), now));
        break;
      case 'xp':
        xpEvents.push({ day: op.row.day, amount: op.row.amount });
        break;
      case 'review': {
        const lang = cards.get(op.row.card_id)?.lang;
        if (lang && op.row.state === 0 && op.row.reviewed_at >= todayStart) {
          newSeenToday[lang] = (newSeenToday[lang] ?? 0) + 1;
        }
        break;
      }
    }
  }
  return { ...snapshot, progress, cards: [...cards.values()], xpEvents, newSeenToday };
}

function toCardRow(card: Card): CardRow {
  return {
    id: card.id,
    kind: card.kind,
    ref_id: card.ref_id,
    lang: card.lang,
    front: card.front,
    back: card.back,
    due: card.due.toISOString(),
    stability: card.stability,
    difficulty: card.difficulty,
    elapsed_days: card.elapsed_days,
    scheduled_days: card.scheduled_days,
    reps: card.reps,
    lapses: card.lapses,
    state: card.state,
    last_review: card.last_review ? card.last_review.toISOString() : null,
  };
}

export function AppDataProvider({ userId, children }: { userId: string; children: ReactNode }) {
  const [data, setData] = useState<Snapshot | null>(() => {
    const stored = readJson<StoredSnapshot>(snapshotKey(userId));
    return stored ? reviveSnapshot(stored) : null;
  });
  const [status, setStatus] = useState<AppDataValue['status']>(data ? 'ready' : 'loading');
  const [error, setError] = useState<string | null>(null);
  // Set only when a refresh fails; showing cached data while a refresh runs is normal.
  const [stale, setStale] = useState(false);
  // Latest snapshot for callbacks that must not re-create on every change.
  const dataRef = useRef(data);
  dataRef.current = data;

  // Persist the snapshot after a quiet moment, and right away when the app goes to the
  // background (iOS may kill a backgrounded PWA without further notice).
  useEffect(() => {
    if (!data) return;
    const save = () => writeJson(snapshotKey(userId), data);
    const timer = window.setTimeout(save, SNAPSHOT_WRITE_DELAY_MS);
    const onHide = () => document.visibilityState === 'hidden' && save();
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', save);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', save);
    };
  }, [data, userId]);

  const update = useCallback((change: (current: Snapshot) => Snapshot) => {
    setData((current) => (current ? change(current) : current));
  }, []);

  const refresh = useCallback(async () => {
    try {
      await flush(userId);
      const snapshot = await loadSnapshot(userId);
      setData(withPending(snapshot, pendingOps(userId), new Date()));
      void syncCourseChoice(userId, snapshot.profile.active_course_id)
        .then((courseId) =>
          update((d) => (d.profile.active_course_id === courseId ? d : { ...d, profile: { ...d.profile, active_course_id: courseId } })),
        )
        .catch(() => {
          // Kept on the device; retried on the next refresh.
        });
      setStale(false);
      setError(null);
      setStatus('ready');
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      setError(message);
      setStatus(dataRef.current ? 'ready' : 'error');
      setStale(dataRef.current !== null);
    }
  }, [update, userId]);

  useEffect(() => {
    void refresh();
    prefetchLeague();
    const onOnline = () => void refresh();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [refresh]);

  const setActiveCourse = useCallback(
    (courseId: string) => {
      writeString(courseChoiceKey(userId), courseId);
      update((d) => ({
        ...d,
        activeCourseId: courseId,
        ...courseSlice(d.allBooks, d.allLessons, courseId),
      }));
      void syncCourseChoice(userId, dataRef.current?.profile.active_course_id ?? null)
        .then((saved) => update((d) => ({ ...d, profile: { ...d.profile, active_course_id: saved } })))
        .catch(() => {
          // Offline: the choice stays on the device and is saved on the next refresh.
        });
    },
    [update, userId],
  );

  const updateProfile = useCallback(
    async (patch: ProfilePatch) => {
      await saveProfile(userId, patch);
      update((d) => ({ ...d, profile: { ...d.profile, ...patch } }));
    },
    [update, userId],
  );

  const addXp = useCallback(
    (amount: number, award: XpAward | null) => {
      if (amount <= 0 || !award) return;
      const day = appDay(new Date());
      update((d) => ({ ...d, xpEvents: [...d.xpEvents, { day, amount }] }));
      queueWrite(userId, { type: 'xp', row: { ...award, amount, day } });
    },
    [update, userId],
  );

  const saveProgress = useCallback(
    (record: ProgressRecord) => {
      update((d) => ({ ...d, progress: { ...d.progress, [record.lesson_id]: record } }));
      queueWrite(userId, { type: 'progress', row: record });
    },
    [update, userId],
  );

  const addCards = useCallback(
    (incoming: readonly NewCard[]): number => {
      const existing = new Set((dataRef.current?.cards ?? []).map((c) => `${c.kind}:${c.ref_id}`));
      const now = new Date();
      const created: Card[] = incoming
        .filter((c) => !existing.has(`${c.kind}:${c.ref_id}`))
        .map((c) => ({ ...c, ...newCardFields(now), id: cardId(userId, c.kind, c.ref_id), suspended: false, created_at: now }));
      if (created.length === 0) return 0;
      update((d) => ({ ...d, cards: [...d.cards, ...created] }));
      created.forEach((card) => queueWrite(userId, { type: 'card', row: toCardRow(card) }));
      return created.length;
    },
    [update, userId],
  );

  const reviewCard = useCallback(
    (card: Card, rating: Rating): Card => {
      const now = new Date();
      const result = review(card, rating, now);
      const updated: Card = { ...card, ...result.card };
      update((d) => ({
        ...d,
        cards: d.cards.map((c) => (c.id === card.id ? updated : c)),
        ...(card.state === CardState.New ? countNewSeen(d, card.lang, appDay(now)) : {}),
      }));
      queueWrite(userId, { type: 'card', row: toCardRow(updated) });
      queueWrite(userId, {
        type: 'review',
        row: {
          card_id: card.id,
          rating: result.log.rating,
          state: result.log.state,
          elapsed_days: result.log.elapsed_days,
          scheduled_days: result.log.scheduled_days,
          reviewed_at: result.log.reviewed_at.toISOString(),
        },
      });
      // Queued after the review_log op: the server only pays for a logged recall.
      addXp(xpForReview(rating, card.state), reviewAward(card.id));
      return updated;
    },
    [addXp, update, userId],
  );

  const activeCourse = useMemo(
    () => data?.courses.find((c) => c.id === data.activeCourseId) ?? null,
    [data],
  );

  const checkAchievements = useCallback(() => {
    // Progress and XP must reach the server first: the check reads them there.
    void flush(userId)
      .catch(() => undefined)
      .then(() => runAchievementCheck());
  }, [userId]);

  const value = useMemo<AppDataValue>(
    () => ({
      status,
      error,
      stale,
      data,
      activeCourse,
      refresh,
      setActiveCourse,
      updateProfile,
      addXp,
      saveProgress,
      addCards,
      reviewCard,
      checkAchievements,
    }),
    [
      status,
      error,
      stale,
      data,
      activeCourse,
      refresh,
      setActiveCourse,
      updateProfile,
      addXp,
      saveProgress,
      addCards,
      reviewCard,
      checkAchievements,
    ],
  );

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
}

export function useAppData(): AppDataValue {
  const value = useContext(AppDataContext);
  if (!value) throw new Error('useAppData must be used inside AppDataProvider');
  return value;
}

/** Snapshot accessor for screens rendered only when data is loaded. */
export function useLoadedData(): Snapshot {
  const { data } = useAppData();
  if (!data) throw new Error('App data is not loaded yet');
  return data;
}
